import { FC, useEffect, useMemo } from "react";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  STATUS_BADGE_TYPE,
} from "../../../../../../../../components";
import {
  CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE,
  MAX_LENGTH_100,
  MAX_LENGTH_75,
} from "../../../../../../../../helpers/constants";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetPurchaseOrdersByClientWithActiveStatus } from "../../../../../../../../api/services/client/clientApis";
import { ClientAutoComplete } from "../../../../../../../../components/clientAutoComplete/ClientAutoComplete";
import {
  getFullName,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import { programOpcoFieldEditableSelector } from "../../../../../../../../redux/slices/program/programSelector";
import {
  ProgramDetail as ProgramDetailType,
  ProgramTabFormKey,
  PurchaseOrder,
} from "../../../../../../modelsAndSchemas/Models";

interface ProgramDetailProps {
  pageId: string;
  programData?: ProgramDetailType;
  isFieldDisabled: boolean;
  isFromCoachPortal?: boolean;
  isEditProgram?: boolean;
}

export const ProgramDetail: FC<ProgramDetailProps> = ({
  pageId,
  programData,
  isFieldDisabled,
  isFromCoachPortal = false,
  isEditProgram = false,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const dispatch = useDispatch();
  const { control, getValues, setValue } = useFormContext();

  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const programOpcoFieldEditable = useSelector(
    programOpcoFieldEditableSelector,
  );

  const {
    data: purchaseOrders,
    isFetching: isPendingPurchaseOrders,
    refetchWithParams: refetchPurchaseOrders,
  } = useGetPurchaseOrdersByClientWithActiveStatus();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isPendingPurchaseOrders: {
          isVisible: isPendingPurchaseOrders,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isPendingPurchaseOrders: { isVisible: false } }));
    };
  }, [isPendingPurchaseOrders]);

  const poNumberList = useMemo(() => {
    if (isNotEmpty(purchaseOrders)) {
      return purchaseOrders?.map((item: PurchaseOrder) => ({
        label: item.poNumber,
        value: item.purchaseOrderId,
      }));
    }
    return [];
  }, [purchaseOrders]);

  useEffect(() => {
    if (isNotEmpty(programData)) {
      refetchPurchaseOrders(
        getValueOrBlank(programData?.clientId),
        STATUS_BADGE_TYPE.active,
      );
    }
  }, [programData?.clientId]);

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.programDetails, key);
  };

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(programSiteCoreKeys.programDetails, key);
  };

  const handleFirstNameLastNameBlur = () => {
    const { firstName, lastName } = getValues();

    setValue(
      ProgramTabFormKey.programName,
      getFullName(firstName, lastName, true),
    );
  };

  const getSelectedActualClientAutoCompleteValue = () => {
    if (isEditProgram) {
      return {
        selectedValue: {
          id: getValueOrBlank(programData?.actualClientId),
          label: getValueOrBlank(programData?.actualClientName),
          labelTwo: getValueOrBlank(programData?.actualClientCode),
        },
      };
    }
  };

  const isClientTypeNCBP = () => {
    return (
      programData?.clientType ===
      CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE
    );
  };

  return (
    <MuiGrid
      container
      className="form-container program-detail"
      data-testid="program-detail"
    >
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.prefix}>
          <MuiSelect
            label={getLabel(programSiteCoreKeys.programDetailsPrefix)}
            options={getDropdownValues(
              programSiteCoreKeys.programDetailsPrefixList,
            )}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${ProgramTabFormKey.prefix}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            disabled={isFromCoachPortal}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.firstName}>
          <MuiTextField
            label={getLabel(programSiteCoreKeys.participantFirstName)}
            value=""
            id={`${pageId}-${ProgramTabFormKey.firstName}`}
            isMandatory
            placeholder={commonLabels.inputPlaceHolder}
            maxLength={MAX_LENGTH_100}
            requiredARIALabel={commonARIALabel.required}
            onBlur={() => handleFirstNameLastNameBlur()}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
            disabled={isFromCoachPortal}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.lastName}>
          <MuiTextField
            label={getLabel(programSiteCoreKeys.participantLastName)}
            value=""
            isMandatory
            id={`${pageId}-${ProgramTabFormKey.lastName}`}
            placeholder={commonLabels.inputPlaceHolder}
            maxLength={MAX_LENGTH_100}
            requiredARIALabel={commonARIALabel.required}
            onBlur={() => handleFirstNameLastNameBlur()}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
            disabled={isFromCoachPortal}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.programName}>
          <MuiTextField
            label={getLabel(programSiteCoreKeys.programName)}
            value=""
            id={`${pageId}-${ProgramTabFormKey.programName}`}
            placeholder={commonLabels.inputPlaceHolder}
            showCloseIcon
            maxLength={MAX_LENGTH_75}
            disabled
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.solutionOffering}
        >
          <MuiTextField
            label={getLabel(programSiteCoreKeys.nextView)}
            id={`${pageId}-${ProgramTabFormKey.solutionOffering}`}
            placeholder={commonLabels.inputPlaceHolder}
            disabled
            value=""
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.programClass}>
          <MuiTextField
            label={getLabel(programSiteCoreKeys.nextProgramClass)}
            id={`${pageId}-${ProgramTabFormKey.programClass}`}
            placeholder={commonLabels.inputPlaceHolder}
            disabled
            value=""
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.businessUnit}>
          <MuiTextField
            label={getLabel(programSiteCoreKeys.businessUnit)}
            value=""
            id={`${pageId}-${ProgramTabFormKey.businessUnit}`}
            placeholder={commonLabels.inputPlaceHolder}
            showCloseIcon
            maxLength={MAX_LENGTH_75}
            disabled={isFieldDisabled || isFromCoachPortal}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.costCentre}>
          <MuiTextField
            label={getLabel(programSiteCoreKeys.costCenter)}
            value=""
            id={`${pageId}-${ProgramTabFormKey.costCentre}`}
            placeholder={commonLabels.inputPlaceHolder}
            showCloseIcon
            maxLength={MAX_LENGTH_75}
            disabled={isFieldDisabled || isFromCoachPortal}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.site}>
          <MuiTextField
            id={`${pageId}-${ProgramTabFormKey.site}`}
            label={getLabel(programSiteCoreKeys.site)}
            placeholder={commonLabels.inputPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
            disabled={isFromCoachPortal}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.clientInformation1}
        >
          <MuiTextField
            id={`${pageId}-${ProgramTabFormKey.clientInformation1}`}
            label={getLabel(programSiteCoreKeys.clientInformation1)}
            placeholder={commonLabels.inputPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
            disabled={isFromCoachPortal}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.clientInformation2}
        >
          <MuiTextField
            id={`${pageId}-${ProgramTabFormKey.clientInformation2}`}
            label={getLabel(programSiteCoreKeys.clientInformation2)}
            placeholder={commonLabels.inputPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
            disabled={isFromCoachPortal}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.clientInformation3}
        >
          <MuiTextField
            id={`${pageId}-${ProgramTabFormKey.clientInformation3}`}
            label={getLabel(programSiteCoreKeys.clientInformation3)}
            placeholder={commonLabels.inputPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
            disabled={isFromCoachPortal}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController control={control} name={ProgramTabFormKey.poNumber}>
          <MuiSelect
            label={getLabel(programSiteCoreKeys.pONumber)}
            options={poNumberList || []}
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramTabFormKey.poNumber}`}
            placeholder={commonLabels.selectPlaceHolder}
            searchPlaceholder={commonLabels.search}
            disabled={isFieldDisabled || isFromCoachPortal}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <FormController
          control={control}
          name={ProgramTabFormKey.dossierNumber}
        >
          <MuiTextField
            id={`${pageId}-${ProgramTabFormKey.dossierNumber}`}
            label={getLabel(programSiteCoreKeys.dossierNumber)}
            placeholder={commonLabels.inputPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
            maxLength={MAX_LENGTH_75}
            disabled={isEditProgram && !programOpcoFieldEditable}
          />
        </FormController>
      </MuiGrid>
      {isClientTypeNCBP() && (
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProgramTabFormKey.actualClientId}
          >
            <ClientAutoComplete
              label={getLabel(programSiteCoreKeys.actualClient)}
              formId={ProgramTabFormKey.actualClientId}
              control={control}
              isActiveClientOnly
              excludeNCBPClient
              disabled={isEditProgram && !programOpcoFieldEditable}
              {...getSelectedActualClientAutoCompleteValue()}
            />
          </FormController>
        </MuiGrid>
      )}
    </MuiGrid>
  );
};
